// First-time setup screen shown to users who just signed up. They have an
// auth.users row but no tenant_member yet. The wizard is a 3-step flow:
//
//   1. Identité    — company name + subdomain (only required fields)
//   2. Détails     — legal name, MF, RC, address, RIB, … (all optional;
//                     users without the info skip the step entirely)
//   3. Modules     — pick which business modules light up on day one;
//                     'sales' is always-on, the rest are togglable
//
// On submit the wizard calls create_tenant_for_current_user with the
// full payload (name/subdomain/mf/phone/email + p_details JSONB +
// p_modules TEXT[]). The RPC bootstraps company_settings (with the
// neutral/light theme defaults), store_settings, module_settings, and
// the tenant_members owner row in a single transaction. Then we
// best-effort sync the new <sub>.gesty.tn hostname to Cloudflare
// Pages, invalidate React Query, and bounce to the dashboard.
//
// Visual layout:
//
//   Mobile (< md):
//     ┌──────────────────────────┐
//     │ brand + progress (top)   │  sticky
//     ├──────────────────────────┤
//     │                          │
//     │ form content (scrolls)   │
//     │                          │
//     ├──────────────────────────┤
//     │ primary CTA (bottom)     │  sticky
//     └──────────────────────────┘
//
//   Desktop (md+):
//     ┌────────────┬─────────────┐
//     │  brand     │  form       │
//     │  hero      │  content    │
//     │  panel     │             │
//     │            │             │
//     │  value     │             │
//     │  props     │             │
//     │            │             │
//     └────────────┴─────────────┘

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@erp/ui';
import {
  Loader2, ArrowRight, ArrowLeft, Check,
  ShoppingCart, ShoppingBag, Warehouse, Banknote, Landmark,
  BookCheck, FileText, Receipt, Zap,
} from '@/components/icons';
import {
  useCreateTenant,
  useCurrentTenant,
  useIsSubdomainTakenForSignup,
} from '@/hooks/use-tenant';
import { useSyncStorefrontDomain } from '@/hooks/use-storefront';
import { useAuth } from '@/lib/auth-context';
import { useAuthService, useDataService } from '@/lib/service-context';
import {
  validateSubdomain,
  normalizeSubdomain,
  RESERVED_SUBDOMAINS,
  TUNISIAN_GOVERNORATES,
  ACTIVITY_SECTORS_I18N,
  DEFAULT_ENABLED_MODULES,
  type ModuleKey,
  type OnboardingCompanyDetails,
} from '@erp/data-access/interfaces';
import { getStorefrontRootDomain } from '@/lib/storefront-domain';

/* -------------------------------------------------------------- */
/*  Types                                                          */
/* -------------------------------------------------------------- */

type Step = 1 | 2 | 3;

interface CompanyForm extends OnboardingCompanyDetails {
  // Step 1 / step 2 share the same form object; null-able fields are
  // tolerated by the RPC's NULLIF(trim(...)) calls.
  phone?: string | null;
  email?: string | null;
}

interface ModuleUiEntry {
  key: ModuleKey;
  icon: React.ReactNode;
  title: string;
  desc: string;
  alwaysOn?: boolean;
}

const STEP_LABELS: Record<Step, string> = {
  1: 'Identité',
  2: 'Détails entreprise',
  3: 'Modules',
};

const STEP_HINTS: Record<Step, string> = {
  1: 'Le nom et l’adresse de votre boutique en ligne.',
  2: 'Informations légales et coordonnées — tout est optionnel.',
  3: 'Choisissez ce que vous voulez utiliser dès le départ.',
};

const MODULE_UI: ModuleUiEntry[] = [
  {
    key: 'sales',
    icon: <ShoppingCart size={20} />,
    title: 'Ventes',
    desc: 'Devis, commandes, BL, factures, avoirs.',
    alwaysOn: true,
  },
  {
    key: 'purchases',
    icon: <ShoppingBag size={20} />,
    title: 'Achats',
    desc: 'Commandes fournisseurs, BR, factures d’achat.',
  },
  {
    key: 'stock',
    icon: <Warehouse size={20} />,
    title: 'Stock & inventaires',
    desc: 'Quantités, mouvements, dépôts, inventaires.',
  },
  {
    key: 'payments',
    icon: <Banknote size={20} />,
    title: 'Paiements',
    desc: 'Encaissements, décaissements, récurrences.',
  },
  {
    key: 'treasury',
    icon: <Landmark size={20} />,
    title: 'Trésorerie',
    desc: 'Comptes bancaires, transactions, soldes.',
  },
  {
    key: 'checks',
    icon: <BookCheck size={20} />,
    title: 'Chèques & Traites',
    desc: 'Suivi des chèques émis et reçus, traites.',
  },
  {
    key: 'withholding',
    icon: <FileText size={20} />,
    title: 'Retenue à la source',
    desc: 'Calcul automatique RAS et certificats.',
  },
  {
    key: 'storefront',
    icon: <ShoppingBag size={20} />,
    title: 'Boutique en ligne',
    desc: 'Site e-commerce sur votre sous-domaine.',
  },
  {
    key: 'caisse',
    icon: <Receipt size={20} />,
    title: 'Caisse / POS',
    desc: 'Point de vente en magasin, sessions de caisse.',
  },
];

/* -------------------------------------------------------------- */
/*  Brand panel — desktop only                                    */
/* -------------------------------------------------------------- */

function BrandPanel({ step }: { step: Step }) {
  return (
    // Sticky to viewport so on a long step-2 form the brand stays visible
    // — the form column scrolls independently underneath. h-screen pegs it
    // to the viewport, top-0 anchors it, overflow-hidden clips the
    // decorative blobs from spilling.
    <aside className="sticky top-0 hidden h-screen overflow-hidden bg-gradient-to-br from-primary via-primary to-primary/80 text-primary-foreground md:flex md:flex-col">
      {/* Decorative grid */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            'linear-gradient(currentColor 1px, transparent 1px), linear-gradient(90deg, currentColor 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />
      {/* Decorative blobs */}
      <div
        aria-hidden
        className="pointer-events-none absolute -end-20 -top-20 size-72 rounded-full bg-white/10 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-24 -start-16 size-80 rounded-full bg-white/10 blur-3xl"
      />

      <div className="relative flex h-full flex-col p-10 lg:p-14">
        {/* Brand */}
        <div className="flex items-center gap-2.5">
          <div className="flex size-9 items-center justify-center rounded-xl bg-white text-sm font-bold text-primary shadow-[0_8px_20px_rgba(15,23,42,0.16)]">
            G
          </div>
          <span className="text-lg font-semibold tracking-tight">Gesty</span>
        </div>

        {/* Hero copy */}
        <div className="mt-12 lg:mt-16">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight lg:text-4xl">
            Votre ERP est prêt
            <br />
            <span className="text-white/80">en moins d&apos;une minute.</span>
          </h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-white/75 lg:text-base">
            Conçu pour les commerçants tunisiens. Facturation conforme,
            boutique en ligne incluse, paiements et stock — le tout en français,
            arabe et anglais.
          </p>
        </div>

        {/* Value props */}
        <ul className="mt-10 space-y-4 lg:mt-12">
          {[
            { icon: <Zap size={16} />, text: 'Configuration en 60 secondes' },
            { icon: <Check size={16} />, text: 'Numérotation séquentielle légale (Tunisie)' },
            { icon: <Check size={16} />, text: 'Calcul automatique de la RAS et de la TVA' },
            { icon: <Check size={16} />, text: 'Boutique en ligne sur votre sous-domaine' },
          ].map((it) => (
            <li key={it.text} className="flex items-center gap-3 text-sm text-white/90">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/20">
                {it.icon}
              </span>
              {it.text}
            </li>
          ))}
        </ul>

        {/* Step ghost — anchors to the bottom of the panel */}
        <div className="mt-auto pt-12">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-white/60">
            <span>Étape {step} sur 3</span>
            <span aria-hidden>·</span>
            <span>{STEP_LABELS[step]}</span>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={cn(
                  'h-1 rounded-full transition-colors',
                  s <= step ? 'bg-white' : 'bg-white/20',
                )}
              />
            ))}
          </div>
        </div>
      </div>
    </aside>
  );
}

/* -------------------------------------------------------------- */
/*  Wizard                                                         */
/* -------------------------------------------------------------- */

export function OnboardingWizard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const create = useCreateTenant();
  const syncDomain = useSyncStorefrontDomain();
  const authService = useAuthService();
  const ds = useDataService();
  const root = getStorefrontRootDomain();

  // Belt-and-braces guard: if the user lands on /welcome with a tenant
  // already attached (e.g. they navigated back, or their session was
  // resumed after onboarding), bounce them straight into the dashboard
  // rather than re-show the wizard.
  //
  // CRITICAL: Rules of Hooks. We CANNOT early-return between hook calls
  // because the conditional render path would call a different number of
  // hooks than the unconditional path → "Rendered fewer hooks than
  // expected" crash the moment the tenant becomes truthy (right after
  // create_tenant_for_current_user resolves and TanStack invalidates
  // the cache). Compute the redirect flag first; render at the end.
  const tenantQuery = useCurrentTenant();
  const tenantAlreadyExists = !!tenantQuery.data;

  // Step state — bounded 1..3.
  const [step, setStep] = useState<Step>(1);

  // Step 1 (required)
  const [name, setName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [subdomainTouched, setSubdomainTouched] = useState(false);

  // Step 2 (optional company details). MF is the only field tracked outside
  // this object — it's a top-level RPC parameter (p_mf), not part of the
  // p_details JSONB bag, kept that way for backwards compatibility with
  // any older caller that doesn't pass details.
  const [details, setDetails] = useState<CompanyForm>({
    legalName: '',
    registrationCode: '',
    address: '',
    city: '',
    postalCode: '',
    governorateCode: '',
    phone: '',
    email: '',
    website: '',
    rib: '',
    bankName: '',
    activitySector: '',
  });
  const [mf, setMf] = useState('');

  // Step 3 (modules)
  const [enabledModules, setEnabledModules] = useState<Set<ModuleKey>>(
    () => new Set(DEFAULT_ENABLED_MODULES),
  );

  const [error, setError] = useState<string | null>(null);
  // Server-side subdomain rejection. Set when the create_tenant RPC comes
  // back with "duplicate key" / "invalid_subdomain" (race: someone took
  // the subdomain between step 1 and submit). Surfaced inline on step 1
  // so the user fixes it where the field actually is — not on step 3.
  // Cleared automatically the moment the user edits the subdomain field.
  const [subdomainServerError, setSubdomainServerError] = useState<string | null>(null);

  // Auto-suggest a subdomain from the company name as the user types — but
  // only until they touch the subdomain field themselves.
  useEffect(() => {
    if (!subdomainTouched) {
      const slug = name
        .toLowerCase()
        .normalize('NFD').replace(/[̀-ͯ]/g, '') // strip diacritics
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 30);
      setSubdomain(slug);
      // The slug just changed — any prior server-side rejection no
      // longer applies, let the live availability check decide again.
      if (subdomainServerError) setSubdomainServerError(null);
    }
  }, [name, subdomainTouched, subdomainServerError]);

  // Real-time validation of the subdomain (format + reserved + taken).
  const formatCheck = useMemo(() => validateSubdomain(subdomain || ''), [subdomain]);
  const taken = useIsSubdomainTakenForSignup(formatCheck.ok ? subdomain : '');
  const subdomainOk =
    formatCheck.ok && taken.isFetched && taken.data === false;

  const subdomainStatus = (() => {
    if (!subdomain) return { kind: 'idle' as const };
    // Server-side rejection wins over the live availability hint —
    // the cached "Disponible" is stale once the RPC has rejected it.
    if (subdomainServerError) {
      return { kind: 'error' as const, msg: subdomainServerError };
    }
    if (!formatCheck.ok) {
      const msg =
        formatCheck.reason === 'too_short'
          ? 'Min. 3 caractères'
          : formatCheck.reason === 'too_long'
            ? 'Max. 30 caractères'
            : formatCheck.reason === 'invalid_format'
              ? 'Lettres, chiffres, tirets uniquement'
              : formatCheck.reason === 'reserved'
                ? 'Sous-domaine réservé'
                : 'Invalide';
      return { kind: 'error' as const, msg };
    }
    if (taken.isFetching) return { kind: 'pending' as const, msg: 'Vérification…' };
    if (taken.data === true) return { kind: 'error' as const, msg: 'Déjà pris' };
    if (subdomainOk) return { kind: 'ok' as const, msg: 'Disponible' };
    return { kind: 'idle' as const };
  })();

  const canAdvanceStep1 =
    name.trim().length >= 2 &&
    subdomainOk &&
    !subdomainServerError &&
    !create.isPending;

  // ----------------------- Submit ----------------------- //

  /**
   * Bounce a subdomain conflict back to step 1 with the error rendered
   * inline next to the field — instead of leaving the user stranded on
   * step 3 with a red banner and forcing them to navigate two steps
   * back to fix the actual problem.
   */
  function bounceToStep1WithSubdomainError(msg: string) {
    setError(null);
    setSubdomainServerError(msg);
    setStep(1);
    // Scroll the form pane to the top so the user actually sees the
    // error + the field that needs fixing on mobile.
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }

  async function submit() {
    setError(null);
    const finalSubdomain = normalizeSubdomain(subdomain) ?? subdomain;

    // The "Disponible" badge on step 1 was cached with staleTime=30 s.
    // By the time the user has filled steps 2 and 3 the cache may be
    // stale and someone else may have grabbed the subdomain — catch
    // that BEFORE we try to mutate and have to unwind a server error.
    try {
      const stillTaken = await ds.tenants.isSubdomainTaken(finalSubdomain);
      if (stillTaken) {
        bounceToStep1WithSubdomainError(
          "Ce sous-domaine vient d'être pris. Choisissez-en un autre.",
        );
        return;
      }
    } catch {
      // If the pre-check itself fails (network blip), let the actual
      // mutation run — the server is the source of truth anyway.
    }

    try {
      // Forward only fields with content; the repo strips empty strings to
      // null but we pre-clean for readability of the network panel.
      const payloadDetails: OnboardingCompanyDetails = {
        legalName: details.legalName ?? null,
        registrationCode: details.registrationCode ?? null,
        address: details.address ?? null,
        city: details.city ?? null,
        postalCode: details.postalCode ?? null,
        governorateCode: details.governorateCode ?? null,
        website: details.website ?? null,
        rib: details.rib ?? null,
        bankName: details.bankName ?? null,
        activitySector: details.activitySector ?? null,
      };

      await create.mutateAsync({
        name: name.trim(),
        subdomain: finalSubdomain,
        mf: mf.trim() || null,
        phone: details.phone?.trim() || null,
        email: details.email?.trim() || null,
        details: payloadDetails,
        modules: Array.from(enabledModules),
      });

      // Best-effort: register the new <sub>.gesty.tn hostname as a
      // custom domain on the lyfty-storefront Pages project so the
      // storefront URL works immediately. If this fails we still let
      // the user into the dashboard — they can re-trigger from
      // Paramètres → Domaine later.
      try {
        const accessToken = await authService.getAccessToken();
        if (accessToken) {
          await syncDomain.mutateAsync({
            kind: 'subdomain',
            old: null,
            new: finalSubdomain,
            accessToken,
          });
        }
      } catch (cfErr) {
        // eslint-disable-next-line no-console
        console.warn('CF Pages sync failed during onboarding:', cfErr);
      }

      // The mutation invalidates the current-tenant query; the next
      // render of ProtectedRoute will see a tenant and let us through.
      navigate('/');
    } catch (e) {
      // Subdomain-related rejections belong on step 1, not as a
      // generic banner under the modules grid.
      const cls = classifyError(e);
      if (cls.kind === 'subdomain_taken') {
        bounceToStep1WithSubdomainError(
          "Ce sous-domaine vient d'être pris. Choisissez-en un autre.",
        );
        return;
      }
      if (cls.kind === 'invalid_subdomain') {
        bounceToStep1WithSubdomainError(
          "Le sous-domaine choisi n'est pas valide.",
        );
        return;
      }
      if (cls.kind === 'already_has_tenant') {
        // The user reached the wizard despite having a tenant — usually
        // because the initial useCurrentTenant query came back null
        // transiently (fresh session right after password reset, an auth
        // token mid-rotation, etc.). The server is the source of truth
        // and just told us they have one. Refetch the tenant, then
        // navigate to the dashboard. ProtectedRoute will read the warm
        // cache and let them through instead of bouncing back here.
        try {
          await queryClient.refetchQueries({ queryKey: ['current-tenant'] });
        } catch {
          // Even if the refetch fails, we still navigate — ProtectedRoute
          // will show its own loading/error state, which is still better
          // than leaving the user staring at the wizard.
        }
        navigate('/', { replace: true });
        return;
      }
      setError(cls.msg);
    }
  }

  // ----------------------- Render ----------------------- //

  // Forward / backward navigation handlers.
  const goBack = () => setStep((s) => (s > 1 ? ((s - 1) as Step) : s));
  const goForward = () => setStep((s) => (s < 3 ? ((s + 1) as Step) : s));

  const primaryCtaLabel =
    step === 3 ? 'Créer ma boutique' : step === 2 ? 'Continuer' : 'Continuer';
  const primaryCtaDisabled =
    create.isPending || (step === 1 && !canAdvanceStep1);
  const onPrimaryCta = step === 3 ? submit : goForward;

  // Conditional render is placed AFTER all hook calls — see the comment
  // by tenantQuery above for why this matters.
  if (tenantAlreadyExists) {
    return <Navigate to="/" replace />;
  }
  // Tenant query still in flight: never flash the wizard form before we
  // know whether this user already has a tenant. Waiting for the query
  // is the difference between "I see onboarding for one frame and then
  // the dashboard" (jarring) and "I see a splash and then the dashboard"
  // (correct). The wizard form would be a destructive UI: it implies
  // the user has work to do that they have already finished.
  if (tenantQuery.isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <div className="relative h-10 w-10">
          <div className="absolute inset-0 animate-ping rounded-full bg-primary/30" />
          <div className="absolute inset-1.5 rounded-full bg-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground md:grid md:grid-cols-[44%_1fr] lg:grid-cols-[480px_1fr]">
      <BrandPanel step={step} />

      {/* Right pane — form */}
      <div className="flex min-h-screen flex-col">
        {/* Mobile-only top bar with brand + progress */}
        <header className="sticky top-0 z-20 border-b bg-background/95 backdrop-blur md:hidden">
          <div className="flex items-center justify-between px-5 pb-4 pt-5">
            <div className="flex items-center gap-2">
              <div className="flex size-7 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground">
                G
              </div>
              <span className="text-base font-semibold tracking-tight">
                Gesty
              </span>
            </div>
            <span className="text-xs font-medium text-muted-foreground">
              Étape {step} / 3
            </span>
          </div>
          <div className="grid grid-cols-3 gap-1.5 px-5 pb-4">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={cn(
                  'h-1 rounded-full transition-colors',
                  s <= step ? 'bg-primary' : 'bg-muted',
                )}
              />
            ))}
          </div>
        </header>

        {/* Scrollable content */}
        <main className="flex-1">
          <div className="mx-auto flex h-full w-full max-w-xl flex-col px-5 py-8 sm:px-8 md:py-12 lg:py-16">
            {/* Back link — desktop only, since mobile uses sticky header */}
            {step > 1 && (
              <button
                type="button"
                onClick={goBack}
                disabled={create.isPending}
                className="mb-6 hidden w-fit items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50 md:inline-flex"
              >
                <ArrowLeft size={14} />
                Retour
              </button>
            )}

            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">
                Étape {step} · {STEP_LABELS[step]}
              </p>
              <h1 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">
                {step === 1
                  ? 'Bienvenue sur Gesty'
                  : step === 2
                    ? 'Présentez votre entreprise'
                    : 'Activez vos modules'}
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {STEP_HINTS[step]}
              </p>
              {user?.email ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  Connecté en tant que{' '}
                  <span className="font-medium text-foreground">{user.email}</span>
                </p>
              ) : null}
            </div>

            {/* Form body */}
            <div className="mt-8 flex-1">
              {step === 1 && (
                <Step1Identity
                  name={name}
                  setName={setName}
                  subdomain={subdomain}
                  setSubdomain={(v) => {
                    setSubdomainTouched(true);
                    // Editing the field clears any server-side rejection so
                    // the live availability check takes over again.
                    if (subdomainServerError) setSubdomainServerError(null);
                    setSubdomain(v);
                  }}
                  subdomainStatus={subdomainStatus}
                  subdomainOk={subdomainOk}
                  root={root}
                />
              )}
              {step === 2 && (
                <Step2Details
                  details={details}
                  setDetails={setDetails}
                  mf={mf}
                  setMf={setMf}
                />
              )}
              {step === 3 && (
                <Step3Modules
                  enabled={enabledModules}
                  setEnabled={setEnabledModules}
                />
              )}

              {error && (
                <div
                  role="alert"
                  className="mt-6 rounded-lg border border-destructive/30 bg-destructive/5 p-3.5 text-sm text-destructive"
                >
                  {error}
                </div>
              )}
            </div>

            {/* Mobile back link below content (optional UX) */}
            {step > 1 && (
              <button
                type="button"
                onClick={goBack}
                disabled={create.isPending}
                className="mt-8 inline-flex w-fit items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50 md:hidden"
              >
                <ArrowLeft size={14} />
                Étape précédente
              </button>
            )}
          </div>
        </main>

        {/* Sticky footer with primary CTA + optional secondary action */}
        <footer className="sticky bottom-0 z-20 border-t bg-background/95 backdrop-blur">
          <div className="mx-auto flex w-full max-w-xl flex-col gap-2 px-5 py-4 sm:px-8 sm:py-5">
            <Button
              type="button"
              size="lg"
              className="h-12 w-full text-base"
              onClick={onPrimaryCta}
              disabled={primaryCtaDisabled}
            >
              {create.isPending ? (
                <Loader2 size={16} className="me-2 animate-spin" />
              ) : null}
              {primaryCtaLabel}
              {!create.isPending ? (
                <ArrowRight size={16} className="ms-2" />
              ) : null}
            </Button>

            {/* "Passer cette étape" — only on step 2 (the optional one) */}
            {step === 2 && !create.isPending && (
              <button
                type="button"
                onClick={goForward}
                className="mx-auto mt-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                Passer cette étape
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Step 1 — Identité                                              */
/* -------------------------------------------------------------- */

interface Step1Props {
  name: string;
  setName: (v: string) => void;
  subdomain: string;
  setSubdomain: (v: string) => void;
  subdomainStatus:
    | { kind: 'idle' }
    | { kind: 'pending'; msg: string }
    | { kind: 'ok'; msg: string }
    | { kind: 'error'; msg: string };
  subdomainOk: boolean;
  root: string;
}

function Step1Identity({
  name, setName, subdomain, setSubdomain,
  subdomainStatus, subdomainOk, root,
}: Step1Props) {
  return (
    <div className="space-y-6">
      {/* Company name */}
      <div className="space-y-1.5">
        <Label htmlFor="ob-name" className="text-sm font-medium">
          Nom de votre entreprise
        </Label>
        <Input
          id="ob-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ex : Ahmed Boutique"
          maxLength={120}
          className="h-12 text-base"
          autoFocus
        />
        <p className="text-xs text-muted-foreground">
          Ce nom apparaîtra sur vos factures et votre boutique en ligne.
        </p>
      </div>

      {/* Subdomain — fully integrated suffix */}
      <div className="space-y-1.5">
        <Label htmlFor="ob-sub" className="text-sm font-medium">
          Adresse de votre boutique
        </Label>
        <div
          className={cn(
            'group flex h-12 items-stretch overflow-hidden rounded-lg border bg-background transition-colors',
            'focus-within:ring-2 focus-within:ring-ring focus-within:border-ring',
            subdomainStatus.kind === 'error'
              ? 'border-destructive/50 focus-within:ring-destructive'
              : subdomainStatus.kind === 'ok'
                ? 'border-emerald-500/40'
                : 'border-input',
          )}
        >
          <input
            id="ob-sub"
            value={subdomain}
            onChange={(e) => {
              const cleaned = e.target.value
                .toLowerCase()
                .replace(/[^a-z0-9-]/g, '');
              setSubdomain(cleaned);
            }}
            placeholder="ahmed-boutique"
            maxLength={30}
            className="min-w-0 flex-1 bg-transparent px-3.5 text-base outline-none placeholder:text-muted-foreground/60"
          />
          <div className="flex shrink-0 select-none items-center bg-muted/60 px-3.5 text-sm text-muted-foreground">
            .{root}
          </div>
        </div>

        {/* Status row — reserves height to avoid layout jumps */}
        <div className="flex min-h-[1.25rem] items-center gap-1.5 text-xs">
          {subdomainStatus.kind === 'ok' && (
            <span className="inline-flex items-center gap-1.5 font-medium text-emerald-600 dark:text-emerald-400">
              <Check size={12} className="shrink-0" />
              {subdomainStatus.msg}
              {subdomainOk && (
                <span className="text-muted-foreground">
                  · https://{subdomain}.{root}
                </span>
              )}
            </span>
          )}
          {subdomainStatus.kind === 'error' && (
            <span className="font-medium text-destructive">
              {subdomainStatus.msg}
            </span>
          )}
          {subdomainStatus.kind === 'pending' && (
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Loader2 size={12} className="shrink-0 animate-spin" />
              {subdomainStatus.msg}
            </span>
          )}
          {subdomainStatus.kind === 'idle' && (
            <span className="text-muted-foreground">
              Lettres, chiffres et tirets — 3 à 30 caractères.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Step 2 — Détails entreprise (tout optionnel)                   */
/* -------------------------------------------------------------- */

interface Step2Props {
  details: CompanyForm;
  setDetails: React.Dispatch<React.SetStateAction<CompanyForm>>;
  mf: string;
  setMf: (v: string) => void;
}

function Step2Details({ details, setDetails, mf, setMf }: Step2Props) {
  const update = <K extends keyof CompanyForm>(key: K, value: CompanyForm[K]) =>
    setDetails((d) => ({ ...d, [key]: value }));

  return (
    <div className="space-y-8">
      <Section title="Identité légale" hint="Ce qui sortira sur vos factures officielles.">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Raison sociale" hint="Si différente du nom commercial">
            <Input
              value={details.legalName ?? ''}
              onChange={(e) => update('legalName', e.target.value)}
              placeholder="SARL Ahmed Boutique"
              className="h-11"
            />
          </Field>
          <Field label="Secteur d'activité">
            <Select
              value={details.activitySector ?? ''}
              onValueChange={(v) => update('activitySector', v || null)}
            >
              <SelectTrigger className="h-11">
                <SelectValue placeholder="Choisir un secteur" />
              </SelectTrigger>
              <SelectContent>
                {ACTIVITY_SECTORS_I18N.map((s) => (
                  <SelectItem key={s} value={s}>
                    {ACTIVITY_LABEL_FR[s] ?? s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Matricule fiscal (MF)">
            <Input
              value={mf}
              onChange={(e) => setMf(e.target.value)}
              placeholder="1234567/X/A/M/000"
              className="h-11 font-mono text-sm"
            />
          </Field>
          <Field label="Registre de commerce (RC)">
            <Input
              value={details.registrationCode ?? ''}
              onChange={(e) => update('registrationCode', e.target.value)}
              placeholder="B0123456 2026"
              className="h-11"
            />
          </Field>
        </div>
      </Section>

      <Section title="Contact">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Téléphone">
            <Input
              type="tel"
              value={details.phone ?? ''}
              onChange={(e) => update('phone', e.target.value)}
              placeholder="+216 …"
              className="h-11"
            />
          </Field>
          <Field label="Email professionnel">
            <Input
              type="email"
              value={details.email ?? ''}
              onChange={(e) => update('email', e.target.value)}
              placeholder="contact@maboutique.tn"
              className="h-11"
            />
          </Field>
          <Field label="Site web" className="sm:col-span-2">
            <Input
              type="url"
              value={details.website ?? ''}
              onChange={(e) => update('website', e.target.value)}
              placeholder="https://www.maboutique.tn"
              className="h-11"
            />
          </Field>
        </div>
      </Section>

      <Section title="Adresse">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Adresse" className="sm:col-span-2">
            <Input
              value={details.address ?? ''}
              onChange={(e) => update('address', e.target.value)}
              placeholder="12 rue de la République"
              className="h-11"
            />
          </Field>
          <Field label="Ville">
            <Input
              value={details.city ?? ''}
              onChange={(e) => update('city', e.target.value)}
              placeholder="Sfax"
              className="h-11"
            />
          </Field>
          <Field label="Code postal">
            <Input
              value={details.postalCode ?? ''}
              onChange={(e) => update('postalCode', e.target.value)}
              placeholder="3000"
              maxLength={6}
              className="h-11"
            />
          </Field>
          <Field label="Gouvernorat" className="sm:col-span-2">
            <Select
              value={details.governorateCode ?? ''}
              onValueChange={(v) => update('governorateCode', v || null)}
            >
              <SelectTrigger className="h-11">
                <SelectValue placeholder="Choisir un gouvernorat" />
              </SelectTrigger>
              <SelectContent>
                {TUNISIAN_GOVERNORATES.map((g) => (
                  <SelectItem key={g.code} value={g.code}>
                    {g.nameFr}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
      </Section>

      <Section title="Coordonnées bancaires" hint="Affichées sur vos factures pour les virements.">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Banque">
            <Input
              value={details.bankName ?? ''}
              onChange={(e) => update('bankName', e.target.value)}
              placeholder="BIAT, Attijari, BNA…"
              className="h-11"
            />
          </Field>
          <Field label="RIB (20 chiffres)">
            <Input
              value={details.rib ?? ''}
              onChange={(e) => update('rib', e.target.value)}
              placeholder="08 100 1234567890123 45"
              maxLength={28}
              className="h-11 font-mono text-sm"
            />
          </Field>
        </div>
      </Section>
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Step 3 — Modules                                               */
/* -------------------------------------------------------------- */

interface Step3Props {
  enabled: Set<ModuleKey>;
  setEnabled: React.Dispatch<React.SetStateAction<Set<ModuleKey>>>;
}

function Step3Modules({ enabled, setEnabled }: Step3Props) {
  const toggle = (key: ModuleKey, alwaysOn: boolean | undefined) => {
    if (alwaysOn) return; // sales is locked on
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const enabledCount = MODULE_UI.filter((m) => enabled.has(m.key) || m.alwaysOn).length;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between rounded-lg border bg-muted/40 px-4 py-3 text-sm">
        <span className="text-muted-foreground">
          Modules activés
        </span>
        <span className="font-semibold text-foreground">
          {enabledCount} <span className="text-muted-foreground">/ {MODULE_UI.length}</span>
        </span>
      </div>

      {/* Single column on phones AND on tablets where the brand panel
          eats half the width — only switch to 2 columns at xl, where
          the form pane has ≥ ~640 px of breathing room. */}
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        {MODULE_UI.map((m) => {
          const isOn = enabled.has(m.key) || m.alwaysOn;
          return (
            <div
              key={m.key}
              role="button"
              tabIndex={m.alwaysOn ? -1 : 0}
              aria-pressed={isOn}
              aria-disabled={m.alwaysOn}
              onClick={() => toggle(m.key, m.alwaysOn)}
              onKeyDown={(e) => {
                if (m.alwaysOn) return;
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  toggle(m.key, m.alwaysOn);
                }
              }}
              className={cn(
                'group relative rounded-xl border p-4 text-start transition-all',
                'flex items-start gap-3.5',
                isOn
                  ? 'border-primary bg-primary/[0.04] shadow-sm'
                  : 'border-border bg-background hover:border-foreground/20 hover:bg-muted/40',
                m.alwaysOn
                  ? 'cursor-default'
                  : 'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              )}
            >
              <div
                className={cn(
                  'mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg transition-colors',
                  isOn
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground group-hover:bg-foreground/10',
                )}
              >
                {m.icon}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold leading-tight">
                    {m.title}
                  </span>
                  {m.alwaysOn && (
                    <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      Inclus
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  {m.desc}
                </p>
              </div>

              {/* Toggle indicator */}
              <span
                aria-hidden
                className={cn(
                  'mt-1 flex size-5 shrink-0 items-center justify-center rounded-full border transition-colors',
                  isOn
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-input bg-background text-transparent',
                )}
              >
                <Check size={12} />
              </span>
            </div>
          );
        })}
      </div>

      <p className="text-xs leading-relaxed text-muted-foreground">
        Vous pouvez activer ou désactiver les modules à tout moment depuis{' '}
        <span className="font-medium text-foreground">Paramètres → Modules</span>.
      </p>
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Internal helpers                                               */
/* -------------------------------------------------------------- */

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-4">
        <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
        {hint && (
          <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
        )}
      </div>
      {children}
    </section>
  );
}

function Field({
  label,
  hint,
  children,
  className,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label className="text-xs font-medium text-muted-foreground">
        {label}
        {hint && (
          <span className="ms-1 font-normal text-muted-foreground/70">
            · {hint}
          </span>
        )}
      </Label>
      {children}
    </div>
  );
}

const ACTIVITY_LABEL_FR: Record<string, string> = {
  retail: 'Commerce de détail',
  wholesale: 'Commerce de gros',
  manufacturing: 'Industrie / fabrication',
  construction: 'BTP / construction',
  food_beverage: 'Restauration / agroalimentaire',
  agriculture: 'Agriculture',
  transport_logistics: 'Transport / logistique',
  it_services: 'Informatique / services IT',
  consulting: 'Conseil',
  health: 'Santé',
  education: 'Éducation / formation',
  hospitality: 'Hôtellerie / tourisme',
  beauty_personal_care: 'Beauté / soins',
  automotive: 'Automobile',
  real_estate: 'Immobilier',
  other: 'Autre',
};

type ErrorClassification =
  | { kind: 'subdomain_taken'; msg: string }
  | { kind: 'invalid_subdomain'; msg: string }
  | { kind: 'already_has_tenant'; msg: string }
  | { kind: 'not_authenticated'; msg: string }
  | { kind: 'generic'; msg: string };

/**
 * Classify a thrown error from the create_tenant RPC into a known
 * category so the caller can route it to the right place in the UI
 * (subdomain errors → step 1, generic errors → bottom banner, etc.).
 */
function classifyError(e: unknown): ErrorClassification {
  // Supabase-js wraps PostgREST errors in plain objects (not Error instances)
  // with `.message`/`.code`/`.details`/`.hint`. Extract whichever we can.
  const obj = (e ?? {}) as { message?: unknown; code?: unknown; details?: unknown; hint?: unknown };
  const msg = String(
    (e instanceof Error && e.message) ||
      (typeof obj.message === 'string' && obj.message) ||
      (typeof obj.details === 'string' && obj.details) ||
      e ||
      'unknown',
  );
  const code = typeof obj.code === 'string' ? obj.code : '';

  if (msg.includes('already_has_tenant')) {
    return { kind: 'already_has_tenant', msg: 'Vous avez déjà une boutique. Rechargez la page.' };
  }
  if (msg.includes('invalid_subdomain')) {
    return { kind: 'invalid_subdomain', msg: "Le sous-domaine choisi n'est pas valide." };
  }
  if (msg.includes('duplicate key') || msg.includes('23505') || code === '23505') {
    return { kind: 'subdomain_taken', msg: "Ce sous-domaine vient d'être pris. Choisissez-en un autre." };
  }
  if (msg.includes('not_authenticated')) {
    return { kind: 'not_authenticated', msg: 'Votre session a expiré. Reconnectez-vous.' };
  }
  return { kind: 'generic', msg: `Erreur : ${msg.slice(0, 200)}` };
}

// Re-export the helper so the wizard's reserved list matches what the
// admin's subdomain field uses.
void RESERVED_SUBDOMAINS;
